import { operationalRuleType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { ArrowLeft, Plus, Trash2 } from "react-feather"
import { useFieldArray, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import type * as z from "zod"
import {
  type OperationRuleCreateBody,
  type OperationRuleCreateBodyDesignatedContractorsItem,
  type OperationRuleList200DefaultRule,
  operationRuleApi,
} from "@/api/client/operation-rule"
import { propertyApi } from "@/api/client/property"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLabel,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { Label } from "@/components/ui/label/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { Textarea } from "@/components/ui/textarea/textarea"
import { operationalRuleHistoryPath } from "./history/OperationalRuleHistoryPage"
import { REQUEST_METHOD_OPTIONS, ruleSummary } from "./labels"

const {
  AGENTCALL_CONSENT_AGREE,
  AGENTCALL_CONSENT_OPTIONS,
  DESIGNATED_CONTRACTOR_EXISTS,
  DESIGNATED_CONTRACTOR_NONE,
  DESIGNATED_CONTRACTOR_OPTIONS,
  CONTRACTOR_CATEGORIES,
  ARRANGEMENT_LIMIT_OPTIONS,
  ARRANGEMENT_LIMIT_ALL_ESTIMATE,
  operationalRuleZ,
} = operationalRuleType

const formSchema = operationalRuleZ

type FormValues = z.infer<typeof formSchema>

const DESIGNATED_CONTRACTOR_NOTE_TEMPLATE = `メール：
FAX：
上記以外の連絡先電話番号：
営業時間：
定休日：
夜間対応の可否：
土日祝対応の可否：
その他：`

const emptyContractor = {
  name: "",
  contactPerson: "",
  categories: [] as string[],
  otherCategory: "",
  contact: "",
  requestMethod: "",
  address: "",
  note: DESIGNATED_CONTRACTOR_NOTE_TEMPLATE,
}

// APIレスポンスからフォーム値へ変換
function mapApiToFormValues(
  operationRule: NonNullable<OperationRuleList200DefaultRule>
): FormValues {
  return {
    agentcallConsent: operationRule.agentCallContractorApproval,
    designatedContractorStatus: operationRule.hasDesignatedContractor,
    arrangementLimit: operationRule.arrangementLimit || ARRANGEMENT_LIMIT_ALL_ESTIMATE,
    designatedContractors:
      operationRule.designatedContractors && operationRule.designatedContractors.length > 0
        ? operationRule.designatedContractors.map((contractor) => ({
            name: contractor.name,
            contactPerson: contractor.contactPerson || "",
            categories: contractor.categories,
            otherCategory: contractor.otherCategory || "",
            contact: contractor.contactInfo,
            requestMethod: contractor.requestMethod,
            address: contractor.address || "",
            note: contractor.note || DESIGNATED_CONTRACTOR_NOTE_TEMPLATE,
          }))
        : [emptyContractor],
  }
}

// フォーム値からAPIリクエストへ変換
function mapFormValuesToApi(values: FormValues): OperationRuleCreateBody {
  const hasDesignatedContractor = values.designatedContractorStatus === DESIGNATED_CONTRACTOR_EXISTS

  return {
    agentCallContractorApproval: values.agentcallConsent,
    hasDesignatedContractor: values.designatedContractorStatus,
    arrangementLimit: values.arrangementLimit,
    designatedContractors: hasDesignatedContractor
      ? (values.designatedContractors.map((contractor) => ({
          name: contractor.name,
          contactPerson: contractor.contactPerson || undefined,
          categories: contractor.categories,
          otherCategory: contractor.otherCategory || undefined,
          contactInfo: contractor.contact,
          requestMethod: contractor.requestMethod,
          address: contractor.address || undefined,
          note: contractor.note || undefined,
        })) as OperationRuleCreateBodyDesignatedContractorsItem[])
      : [],
  }
}

const DEFAULT_FORM_VALUES: FormValues = {
  agentcallConsent: AGENTCALL_CONSENT_AGREE,
  designatedContractorStatus: DESIGNATED_CONTRACTOR_NONE,
  designatedContractors: [emptyContractor],
  arrangementLimit: ARRANGEMENT_LIMIT_ALL_ESTIMATE,
} as FormValues

// 運用ルールの適用範囲。null=オーナー既定(全物件共通)、数値=物件固有(E-13)。
const OWNER_DEFAULT_SCOPE = "owner_default"

export function OperationalRulePage() {
  const navigate = useNavigate()

  // 編集対象スコープ。物件を選ぶとその物件固有ルールを編集する(E-13)。
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)

  // 削除確認ダイアログの対象(物件固有ルール)(E-13)。
  const [deleteTarget, setDeleteTarget] = useState<{
    propertyId: number
    propertyName: string
  } | null>(null)

  // オーナーの物件一覧(物件セレクタ用)
  const { data: propertyListData } = propertyApi.usePropertyList({ limit: 1000 }, undefined)
  const properties = propertyListData?.properties ?? []

  // 登録済み運用ルールの一覧(既定 + 物件固有)。俯瞰表示と編集フォームの初期値に使う(E-13)。
  const {
    data: ruleListData,
    isLoading,
    refetch: refetchRuleList,
  } = operationRuleApi.useOperationRuleList()
  const defaultRule = ruleListData?.defaultRule ?? null
  const propertyRules = ruleListData?.propertyRules ?? []

  // 編集対象は選択スコープの現在値そのもの。物件固有が未設定なら既定へはフォールバックしない。
  const operationRule =
    selectedPropertyId == null
      ? defaultRule
      : (propertyRules.find((rule) => rule.propertyId === selectedPropertyId)?.operationRule ??
        null)

  const createMutation = operationRuleApi.useOperationRuleCreate({
    mutation: {
      onSuccess: async () => {
        await refetchRuleList()
        toast.success("運用ルールを更新しました")
        navigate("/profile")
      },
      onError: (error) => {
        toast.error("運用ルールの更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  // 物件固有ルールの削除(既定に戻す)(E-13)。
  // onError は呼び出し時ではなくここで渡す（共通のトーストと二重にしないため）
  const deleteMutation = operationRuleApi.useOperationRuleDeleteForProperty({
    mutation: {
      onError: (error) => {
        toast.error("物件個別ルールの削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    const { propertyId } = deleteTarget
    deleteMutation.mutate(
      { propertyId },
      {
        onSuccess: async () => {
          toast.success("物件個別ルールを削除しました")
          // 削除した物件を編集中だった場合は既定に戻す。
          if (selectedPropertyId === propertyId) {
            setSelectedPropertyId(null)
          }
          setDeleteTarget(null)
          await refetchRuleList()
        },
      }
    )
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  })

  // 既存データをフォームに反映。選択スコープに該当する既存ルールが無ければ初期値に戻す(E-13)。
  useEffect(() => {
    if (!ruleListData) return
    if (operationRule) {
      form.reset(mapApiToFormValues(operationRule))
    } else {
      form.reset(DEFAULT_FORM_VALUES)
    }
  }, [ruleListData, operationRule, form])

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "designatedContractors",
  })

  const watchDesignatedContractorStatus = form.watch("designatedContractorStatus")

  const showContractorForm = watchDesignatedContractorStatus === DESIGNATED_CONTRACTOR_EXISTS

  const handleSubmit = async (values: FormValues) => {
    const apiData = mapFormValuesToApi(values)
    // 選択スコープ(オーナー既定 or 物件固有)に対して運用ルールを作成する(E-13)。
    createMutation.mutate({
      data: { ...apiData, propertyId: selectedPropertyId },
    })
  }

  const handleCancel = () => {
    navigate("/profile")
  }

  const handleAddContractor = () => {
    append(emptyContractor)
  }

  const handleRemoveContractor = (index: number) => {
    remove(index)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <span className="text-sm text-slate-500">読み込み中...</span>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
          <ArrowLeft className="size-4" />
          戻る
        </Button>
        <h1 className="text-base font-semibold text-primary">運用ルール設定</h1>
      </div>

      {/* 登録済み運用ルールの俯瞰一覧(既定 + 物件固有)(E-13) */}
      <Card>
        <CardContent className="px-4 py-4 space-y-3">
          <Label className="text-sm font-semibold text-slate-600">登録済みの運用ルール</Label>
          <div className="divide-y divide-slate-100 rounded-md border">
            {/* 既定(全体)ルール */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700">既定（すべての物件に適用）</p>
                <p className="truncate text-xs text-slate-400">
                  {defaultRule ? ruleSummary(defaultRule) : "未設定"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPropertyId(null)}
                >
                  編集
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="既定ルールの履歴"
                  onClick={() => navigate(operationalRuleHistoryPath(null))}
                >
                  履歴
                </Button>
              </div>
            </div>

            {/* 物件個別ルール */}
            {propertyRules.map((item) => (
              <div
                key={item.propertyId}
                className="flex items-center justify-between gap-2 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {item.propertyName}
                    <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600">
                      個別
                    </span>
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {ruleSummary(item.operationRule)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPropertyId(item.propertyId)}
                  >
                    編集
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`${item.propertyName}の個別ルールの履歴`}
                    onClick={() => navigate(operationalRuleHistoryPath(item.propertyId))}
                  >
                    履歴
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`${item.propertyName}の個別ルールを削除`}
                    onClick={() =>
                      setDeleteTarget({
                        propertyId: item.propertyId,
                        propertyName: item.propertyName,
                      })
                    }
                  >
                    <Trash2 className="size-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            物件個別ルールが無い物件には既定ルールが適用されます。
          </p>
        </CardContent>
      </Card>

      {/* 適用範囲(オーナー既定 / 物件固有)の選択(E-13) */}
      <Card>
        <CardContent className="px-4 py-4 space-y-2">
          <Label className="text-sm font-semibold text-slate-600">設定する対象</Label>
          <Select
            value={selectedPropertyId == null ? OWNER_DEFAULT_SCOPE : String(selectedPropertyId)}
            onValueChange={(value) =>
              setSelectedPropertyId(value === OWNER_DEFAULT_SCOPE ? null : Number(value))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="対象を選択してください" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OWNER_DEFAULT_SCOPE}>既定（すべての物件に適用）</SelectItem>
              {properties.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>
                  {property.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">
            物件を選ぶとその物件専用のルールを設定できます。物件専用ルールが無い物件には既定ルールが適用されます。
          </p>
        </CardContent>
      </Card>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          {/* 業者手配に関する確認 */}
          <Card>
            <CardContent className="pt-0 px-0 pb-5">
              <div className="bg-slate-100 px-3.5 py-3 mb-4">
                <h2 className="text-sm font-bold text-primary">業者手配に関する確認</h2>
              </div>
              <div className="px-4 space-y-4">
                <div className="space-y-2.5 pb-2.5">
                  <p className="text-sm font-semibold text-slate-600">
                    エージェントコール業者の手配について
                  </p>
                  <div className="text-xs font-medium text-slate-600 space-y-2">
                    <p>
                      案件の対応を行うことができるオーナー様指定業者の登録がない場合、当社にてエージェントコール業者を手配いたします。
                    </p>
                    <p>
                      3万円（税抜）未満 の場合：オーナー様承諾不要で手配を進めます。
                      <br />
                      3万円（税抜）以上 の場合：見積をご提示のうえ承諾をいただきます。
                    </p>
                    <div className="space-y-1 text-red-500">
                      <p>※原則、見積取得には費用は発生しません。</p>
                      <p>
                        ※「承諾する」にチェックを入れた場合でもオーナー様指定業者が登録されている場合はオーナー様指定業者優先で手配いたします。
                        <br />
                        ただし、問い合わせ内容に対して該当カテゴリの業者登録がない場合はエージェントコール業者を手配いたします。
                      </p>
                      <p className="text-slate-900">
                        ※対応エリア表は
                        <a
                          href="https://docs.google.com/spreadsheets/d/1ep8easncd3DKEiy_kON0iq4cmzAaXVUAzP7tUksh9DY/edit?gid=0#gid=0"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          こちら
                        </a>
                        を参照してください。
                      </p>
                    </div>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="agentcallConsent"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="space-y-2"
                        >
                          {AGENTCALL_CONSENT_OPTIONS.map((option) => (
                            <div key={option.key} className="flex items-start gap-2">
                              <RadioGroupItem value={option.key} id={`consent-${option.key}`} />
                              <Label
                                htmlFor={`consent-${option.key}`}
                                className="text-sm font-medium text-primary cursor-pointer leading-[18px]"
                              >
                                {option.value}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* オーナー様指定業者の有無 */}
          <Card>
            <CardContent className="pt-0 px-0 pb-5">
              <div className="bg-slate-100 px-3.5 py-3 mb-4">
                <h2 className="text-sm font-bold text-primary">オーナー様指定業者の有無について</h2>
              </div>
              <div className="px-4 space-y-4">
                <div className="pb-2.5">
                  <p className="text-xs font-medium text-slate-600">
                    ご指定の業者様がある場合は、カテゴリごとにご登録いただけます。
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="designatedContractorStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="space-y-2"
                        >
                          {DESIGNATED_CONTRACTOR_OPTIONS.map((option) => (
                            <div key={option.key} className="flex items-center gap-2">
                              <RadioGroupItem value={option.key} id={`designated-${option.key}`} />
                              <Label
                                htmlFor={`designated-${option.key}`}
                                className="text-sm font-medium text-primary cursor-pointer"
                              >
                                {option.value}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* 指定業者登録フォーム */}
          {showContractorForm && (
            <Card>
              <CardContent className="pt-0 px-0 pb-5">
                <div className="bg-slate-100 px-3.5 py-3 mb-4">
                  <h2 className="text-sm font-bold text-primary">指定業者登録フォーム</h2>
                </div>
                <div className="px-4 space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="space-y-4">
                      {index > 0 && (
                        <div className="flex items-center gap-2.5 py-2.5">
                          <div className="flex-1 h-px bg-slate-300" />
                          <span className="text-sm font-semibold text-slate-300">追加項目</span>
                          <div className="flex-1 h-px bg-slate-300" />
                        </div>
                      )}

                      {/* 業者名 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-600">業者名</span>
                              <FormRequiredLabel />
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* 担当者名 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.contactPerson`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-semibold text-slate-600">担当者名</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* カテゴリ */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.categories`}
                        render={() => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-600">
                                カテゴリ（複数選択可）
                              </span>
                              <FormRequiredLabel />
                            </FormLabel>
                            <div className="flex flex-wrap gap-1.5">
                              {CONTRACTOR_CATEGORIES.map((category) => (
                                <FormField
                                  key={category.key}
                                  control={form.control}
                                  name={`designatedContractors.${index}.categories`}
                                  render={({ field }) => (
                                    <FormItem className="w-[155px] flex items-center gap-1.5 py-1.5">
                                      <FormControl>
                                        <Checkbox
                                          id={`category-${index}-${category.key}`}
                                          checked={field.value?.includes(category.key)}
                                          onCheckedChange={(checked) => {
                                            const currentValue = field.value || []
                                            if (checked) {
                                              field.onChange([...currentValue, category.key])
                                            } else {
                                              field.onChange(
                                                currentValue.filter(
                                                  (value) => value !== category.key
                                                )
                                              )
                                            }
                                          }}
                                        />
                                      </FormControl>
                                      <Label
                                        htmlFor={`category-${index}-${category.key}`}
                                        className="text-sm font-medium text-slate-600 cursor-pointer !mt-0"
                                      >
                                        {category.value}
                                      </Label>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* その他カテゴリ */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.otherCategory`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-semibold text-slate-600">
                              その他（カテゴリを入力してください）
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* 連絡先 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.contact`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-600">連絡先（電話）</span>
                              <FormRequiredLabel />
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* 依頼方法 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.requestMethod`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-600">依頼方法</span>
                              <FormRequiredLabel />
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="選択してください" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {REQUEST_METHOD_OPTIONS.map((option) => (
                                  <SelectItem key={option.key} value={option.key}>
                                    {option.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            <p className="text-xs text-slate-400">
                              ※依頼方法でメールFAXを選択している場合は備考にメールFAXの記載をお願いします。
                            </p>
                          </FormItem>
                        )}
                      />

                      {/* 住所 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.address`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-semibold text-slate-600">住所</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* 備考 */}
                      <FormField
                        control={form.control}
                        name={`designatedContractors.${index}.note`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-semibold text-slate-600">備考</FormLabel>
                            <FormControl>
                              <Textarea {...field} rows={3} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* 削除ボタン */}
                      {index > 0 && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveContractor(index)}
                          >
                            <Trash2 className="size-3.5" />
                            削除
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 追加ボタン */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleAddContractor}
                  >
                    <Plus className="size-3.5" />
                    他の指定業者を追加
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 手配上限金額の設定 */}
          <Card>
            <CardContent className="pt-0 px-0 pb-5">
              <div className="bg-slate-100 px-3.5 py-3 mb-4">
                <h2 className="text-sm font-bold text-primary">
                  手配上限金額の設定（オーナー様指定業者）
                </h2>
              </div>
              <div className="px-4 space-y-2">
                <FormField
                  control={form.control}
                  name="arrangementLimit"
                  render={({ field }) => (
                    <FormItem>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ARRANGEMENT_LIMIT_OPTIONS.map((option) => (
                            <SelectItem key={option.key} value={option.key}>
                              {option.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <p className="text-xs font-medium text-red-500 leading-4">
                  ※手配上限金額を業者に伝え、金額内は対応完了まで進めてもらうようになります。
                  <br />
                  ※上限金額を超える場合には見積をご提示いたします。
                </p>
              </div>
            </CardContent>
          </Card>

          {/* お問い合わせ案内 */}
          <p className="text-sm font-medium text-primary">
            ご不明点はオペレーションセンターまでお問い合わせください。
          </p>

          {/* Actions */}
          <div className="flex gap-2.5 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createMutation.isPending}
            >
              キャンセル
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "更新中..." : "更新する"}
            </Button>
          </div>
        </form>
      </Form>

      {/* 物件個別ルールの削除確認(既定に戻す)(E-13) */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">物件個別ルールの削除</DialogTitle>
            <DialogDescription>
              「{deleteTarget?.propertyName}」の物件個別ルールを削除します。
              削除後はこの物件にも既定（すべての物件に適用）のルールが適用されます。よろしいですか？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "削除中..." : "削除する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
